import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { TagIcon } from "./TagIcon";
import { X, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTagAssignment } from "@/hooks/useTagAssignment";
import { getTagsByCustomerId } from "@/utils/customerTags";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Tag {
  id: string;
  name: string;
  color: string;
  icon?: string;
  type: string;
}

interface TagsDisplayProps {
  resourceId: string;
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user";
  maxTags?: number;
  size?: "sm" | "default";
  className?: string;
  prefetchedTags?: Tag[];
  showRemoveButton?: boolean;
  onTagRemoved?: () => void;
  customerIds?: string[];
  assignedTagIds?: string[];
  useMockData?: boolean;
}

export const TagsDisplay = ({
  resourceId,
  resourceType,
  maxTags = 3,
  size = "default",
  className = "",
  prefetchedTags,
  showRemoveButton = false,
  onTagRemoved,
  customerIds,
  assignedTagIds,
  useMockData
}: TagsDisplayProps) => {
  const [tags, setTags] = useState<Tag[]>(prefetchedTags || []);
  const [isLoading, setIsLoading] = useState(!prefetchedTags);
  const [tagToRemove, setTagToRemove] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();
  const { removeTag, isLoading: isRemoving } = useTagAssignment(resourceType);

  const getJunctionTable = () => {
    switch (resourceType) {
      case "device": return "device_tags";
      case "profile": return "profile_tags";
      case "application": return "application_tags";
      case "configuration": return "configuration_tags";
    }
  };

  const getResourceColumn = () => {
    switch (resourceType) {
      case "device": return "device_id";
      case "profile": return "profile_id";
      case "application": return "application_id";
      case "configuration": return "configuration_id";
    }
  };

  useEffect(() => {
    // Phase 1: Skip fetching if we have pre-fetched tags
    if (prefetchedTags) {
      setTags(prefetchedTags);
      setIsLoading(false);
    } else {
      fetchTags();
    }
  }, [resourceId, prefetchedTags]);

  const fetchTags = async () => {
    try {
      // If explicit mock data props provided, use mock data
      if (useMockData || (customerIds && assignedTagIds)) {
        fetchMockTags();
        return;
      }

      // Try database first
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const { data, error } = await supabase
        .from(junctionTable as any)
        .select(`
          tags!inner (
            id,
            name,
            color,
            icon,
            type
          )
        `)
        .eq(resourceColumn as string, resourceId);

      if (error) throw error;

      const fetchedTags = data?.map((item: any) => item.tags).filter(Boolean) || [];
      setTags(fetchedTags);
    } catch (error: any) {
      const isUuidErr = error?.code === '22P02' || /invalid input syntax for type uuid/i.test(error?.message || '');
      
      // If UUID error AND we have customer/tag data, fall back to mock
      if (isUuidErr && customerIds && assignedTagIds) {
        console.log(`[TagsDisplay] UUID error for ${resourceType} ${resourceId}, falling back to mock data`);
        fetchMockTags();
        return;
      }
      
      // For profile/application, UUID errors are expected for mock IDs
      if ((resourceType === 'profile' || resourceType === 'application') && isUuidErr) {
        // Try mock data fallback if we have the necessary info
        if (customerIds && assignedTagIds) {
          fetchMockTags();
          return;
        }
        setTags([]);
        return;
      }
      
      console.error("Error fetching tags:", error);
      setTags([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMockTags = () => {
    if (!customerIds || !assignedTagIds) {
      console.warn("[TagsDisplay] Mock data requested but customerIds or assignedTagIds missing");
      setTags([]);
      setIsLoading(false);
      return;
    }

    // Get all tags available to these customers
    const availableTags = customerIds.flatMap(customerId => 
      getTagsByCustomerId(customerId)
    );

    // Filter to only assigned tags and convert to Tag format
    const mockTags: Tag[] = availableTags
      .filter(tag => assignedTagIds.includes(tag.id))
      .map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color || '#6b7280',
        icon: undefined,
        type: tag.type
      }));

    setTags(mockTags);
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className={`flex gap-1 ${className}`}>
        <div className="h-5 w-12 bg-muted animate-pulse rounded-full"></div>
        <div className="h-5 w-16 bg-muted animate-pulse rounded-full"></div>
      </div>
    );
  }

  const handleRemoveTag = (tagId: string, tagName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setTagToRemove({ id: tagId, name: tagName });
  };

  const confirmRemoveTag = async () => {
    if (!tagToRemove) return;
    
    // If using mock data, just update local state and notify parent
    if (useMockData || (customerIds && assignedTagIds)) {
      setTags(tags.filter(t => t.id !== tagToRemove.id));
      toast({
        title: "Tag Removed",
        description: `"${tagToRemove.name}" has been removed from this ${resourceType}.`,
      });
      onTagRemoved?.();
      setTagToRemove(null);
      return;
    }

    // Otherwise use database removal
    const success = await removeTag(resourceId, tagToRemove.id);
    if (success) {
      setTags(tags.filter(t => t.id !== tagToRemove.id));
      toast({
        title: "Tag Removed",
        description: `"${tagToRemove.name}" has been removed.`,
      });
      onTagRemoved?.();
    }
    setTagToRemove(null);
  };

  if (tags.length === 0) {
    return null;
  }

  const displayTags = tags.slice(0, maxTags);
  const remainingCount = tags.length - maxTags;

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {displayTags.map(tag => (
        <Badge
          key={tag.id}
          variant="secondary"
          className={`gap-1 group relative ${size === "sm" ? "text-xs px-1.5 py-0.5" : ""} ${showRemoveButton ? "pr-6" : ""}`}
          style={{ 
            backgroundColor: `${tag.color}20`, 
            borderColor: tag.color,
            color: tag.color
          }}
        >
          <TagIcon
            iconName={tag.icon}
            color={tag.color}
            size={size === "sm" ? 6 : 8}
          />
          {tag.name}
          {showRemoveButton && (
            <button
              onClick={(e) => handleRemoveTag(tag.id, tag.name, e)}
              disabled={isRemoving}
              className="absolute right-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 rounded-full p-0.5"
              aria-label={`Remove ${tag.name} tag`}
            >
              <X className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
            </button>
          )}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <Badge
          variant="outline"
          className={size === "sm" ? "text-xs px-1.5 py-0.5" : ""}
        >
          +{remainingCount}
        </Badge>
      )}
      
      <AlertDialog open={!!tagToRemove} onOpenChange={(open) => !open && setTagToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remove Tag?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removing "{tagToRemove?.name}" may trigger configuration changes or deployments to this {resourceType}. This action cannot be undone.
              <br /><br />
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveTag} className="bg-destructive hover:bg-destructive/90">
              Remove Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};